import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { type AnyExtension, Extension, ReactNodeViewRenderer } from "@tiptap/react";
import {
  FRONT_MATTER_NODE_NAME,
  FrontMatter,
  FrontMatterField,
  frontMatterPositions,
} from "@workith/editor-schema";
import { ySyncPluginKey } from "y-prosemirror";
import { FrontMatterFieldNodeView } from "./FrontMatterFieldNodeView";
import { FrontMatterNodeView } from "./FrontMatterNodeView";
import { focusAfterFrontMatter } from "./frontMatterCommands";

const FrontMatterWithView = FrontMatter.extend({
  addNodeView() {
    return ReactNodeViewRenderer(FrontMatterNodeView);
  },
});

const FrontMatterFieldWithView = FrontMatterField.extend({
  addNodeView() {
    return ReactNodeViewRenderer(FrontMatterFieldNodeView);
  },
});

function marksChangeOrigin(meta: unknown): boolean {
  return (meta as { isChangeOrigin?: boolean } | undefined)?.isChangeOrigin === true;
}

/**
 * Yjs 바인딩은 원격 업데이트를 적용할 때 자기 plugin key로 `isChangeOrigin` meta를 붙인다.
 * Tiptap Collaboration은 `@tiptap/y-tiptap`의 바인딩을 쓰고 이 앱은 `y-prosemirror`도 함께
 * 불러오므로, 두 모듈이 각각 만든 `y-sync` PluginKey의 문자열이 서로 달라진다. 상수 하나만
 * 보면 실제 원격 트랜잭션을 놓치므로 실제 등록된 plugin key까지 확인한다.
 * 이 트랜잭션은 절대 거부하지 않는다.
 */
function isRemoteTransaction(tr: Transaction, state: EditorState): boolean {
  if (marksChangeOrigin(tr.getMeta(ySyncPluginKey))) return true;
  return state.plugins.some((plugin) => {
    const key = plugin.spec.key;
    return key !== undefined && marksChangeOrigin(tr.getMeta(key));
  });
}

/**
 * 원격·과거 데이터의 비정상 배치는 절대 고치거나 지우지 않는다. 로컬 트랜잭션이
 * 첫 로컬 카드를 선두가 아닌 곳에 만들거나, 카드 수를 새로 늘리거나,
 * 정상 선두 카드를 뒤로 미는 경우만 거부한다.
 */
export function allowsFrontMatterArrangement(
  before: ProseMirrorNode,
  after: ProseMirrorNode
): boolean {
  const previous = frontMatterPositions(before);
  const next = frontMatterPositions(after);
  if (previous.length === 0 && next.length === 1 && next[0] !== 0) return false;
  if (next.length > previous.length && next.length > 1) return false;
  if (previous.length === 1 && previous[0] === 0 && next.length === 1 && next[0] !== 0) {
    return false;
  }
  return true;
}

/**
 * 카드가 NodeSelection된 상태의 Enter/ArrowUp/ArrowLeft는 카드 앞에 본문을 만들지 않고
 * 카드 뒤 본문으로 나간다. Backspace/Delete는 가로채지 않아 명시적 전체 삭제를 허용한다.
 * 기본 keymap이 `createParagraphNear`로 먼저 처리하지 않도록 우선순위를 올린다.
 */
const FrontMatterGuard = Extension.create({
  name: "frontMatterGuard",
  priority: 1000,

  addKeyboardShortcuts() {
    const leaveSelectedCard = () => {
      const { selection } = this.editor.state;
      if (
        !(selection instanceof NodeSelection) ||
        selection.node.type.name !== FRONT_MATTER_NODE_NAME
      ) {
        return false;
      }
      return focusAfterFrontMatter(this.editor, selection.from);
    };

    return {
      Enter: leaveSelectedCard,
      ArrowUp: leaveSelectedCard,
      ArrowLeft: leaveSelectedCard,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("frontMatterGuard"),
        filterTransaction: (tr, state) => {
          if (!tr.docChanged) return true;
          // 거의 모든 트랜잭션은 여기서 통과한다. plugin 목록을 훑는 원격 판정은
          // 거부 후보에만 필요하므로 뒤에 둔다.
          if (allowsFrontMatterArrangement(state.doc, tr.doc)) return true;
          return isRemoteTransaction(tr, state);
        },
      }),
    ];
  },
});

export function createFrontMatterExtensions(): AnyExtension[] {
  return [FrontMatterWithView, FrontMatterFieldWithView, FrontMatterGuard];
}
